import * as React from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import SettingsService from 'services/SettingsService';
import { ORDER_KITTING_NAMESPACE, OrderKittingSettings, STORES, StoreSettings } from '../types';

// Per-store enable + dry-run/live switches, plus the (global) order-age window
// for the Order Kitting job (runs every 15 minutes). Seeds from the EFFECTIVE
// settings passed in by the page; saves through the generic settings store —
// the job pulls them fresh on every run, no deploy.
export default function ModeDialog({
  open,
  settings,
  onClose,
  onSaved,
}: {
  open: boolean;
  settings: OrderKittingSettings | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const settingsService = React.useMemo(() => new SettingsService(), []);

  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [stores, setStores] = React.useState<Record<string, StoreSettings>>({});
  const [minAge, setMinAge] = React.useState('10');
  const [maxAge, setMaxAge] = React.useState('50');

  React.useEffect(() => {
    if (!open || !settings) return;
    setError(null);
    setStores(settings.stores);
    setMinAge(String(settings.minOrderAgeMinutes));
    setMaxAge(String(settings.maxOrderAgeMinutes));
  }, [open, settings]);

  const minAgeNum = Number(minAge);
  const maxAgeNum = Number(maxAge);
  const minAgeBad = !Number.isInteger(minAgeNum) || minAgeNum < 5 || minAgeNum > 30;
  const maxAgeBad = !Number.isInteger(maxAgeNum) || maxAgeNum < 30 || maxAgeNum > 55;
  const canSave = settings != null && !saving && !minAgeBad && !maxAgeBad;
  const anyLive = STORES.some(store => stores[store]?.enabled && stores[store]?.live);

  const setStore = (store: string, patch: Partial<StoreSettings>) =>
    setStores(prev => ({ ...prev, [store]: { ...prev[store], ...patch } }));

  const save = async () => {
    setSaving(true);
    setError(null);
    const updated = await settingsService.update(ORDER_KITTING_NAMESPACE, {
      stores,
      minOrderAgeMinutes: minAgeNum,
      maxOrderAgeMinutes: maxAgeNum,
    });
    setSaving(false);
    if (!updated) {
      setError('Failed to save settings');
      return;
    }
    onSaved();
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Kitting settings</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          The job runs every 15 minutes and must finish its work inside
          ShipBob&apos;s 60-minute import delay. Enable/live are per store, so
          rollout can go one storefront at a time. Changes apply from the next
          run. LIVE EDITS real customer orders on that store (no customer
          emails, zero payment change enforced); a store without an order-edit
          token stays dry-run even if switched live.
        </Typography>
        {!settings ? (
          <Stack alignItems="center" sx={{ py: 4 }}>
            <CircularProgress size={28} />
          </Stack>
        ) : (
          <Stack spacing={2} sx={{ mt: 1 }}>
            {STORES.map(store => {
              const value = stores[store] ?? { enabled: true, live: false };
              return (
                <Stack key={store} direction="row" alignItems="center" spacing={2}>
                  <Typography sx={{ width: 40, fontWeight: 600 }}>{store}</Typography>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ width: 150 }}>
                    <Switch
                      size="small"
                      checked={value.enabled}
                      onChange={e => setStore(store, { enabled: e.target.checked })}
                    />
                    <Typography variant="body2" color={value.enabled ? undefined : 'text.secondary'}>
                      {value.enabled ? 'Enabled' : 'Disabled'}
                    </Typography>
                  </Stack>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Switch
                      size="small"
                      color="error"
                      checked={value.live}
                      disabled={!value.enabled}
                      onChange={e => setStore(store, { live: e.target.checked })}
                    />
                    <Typography
                      variant="body2"
                      color={value.enabled && value.live ? 'error' : 'text.secondary'}
                    >
                      {value.live ? 'LIVE' : 'Dry-run'}
                    </Typography>
                  </Stack>
                </Stack>
              );
            })}
            <Divider />
            <TextField
              label="Minimum order age (minutes)"
              value={minAge}
              onChange={e => setMinAge(e.target.value)}
              error={minAgeBad}
              helperText="Orders younger than this are left alone (post-purchase upsells settle first). 5–30."
              fullWidth
            />
            <TextField
              label="Maximum order age (minutes)"
              value={maxAge}
              onChange={e => setMaxAge(e.target.value)}
              error={maxAgeBad}
              helperText="Orders older than this are never touched — they may already be in ShipBob. 30–55 (under the 60-minute import delay)."
              fullWidth
            />
          </Stack>
        )}
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color={anyLive ? 'error' : 'primary'}
          disabled={!canSave}
          onClick={save}
        >
          {saving ? 'Saving…' : anyLive ? 'Save (stores go live)' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
